import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { Field, field } from './Field';

/**
 * Taking down a phone call (V5 dogfood — Requests is the validation gate every job enters
 * through, and `request.create` had no button, so the page said work "lands here first"
 * while offering no way to put anything there).
 *
 * This is written for someone holding a phone. Two fields are required — who called and
 * what's wrong — because that is genuinely all you have thirty seconds in, and a form that
 * demands an address before it will save is a form people abandon mid-call and re-type from
 * a sticky note later.
 */
export function LogCallSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [addressText, setAddressText] = useState('');
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [urgency, setUrgency] = useState<'low' | 'normal' | 'emergency'>('normal');

  const create = useMutation(
    trpc.request.create.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries();
        setContactName('');
        setContactPhone('');
        setContactEmail('');
        setAddressText('');
        setTitle('');
        setDetails('');
        setUrgency('normal');
        onClose();
      },
    }),
  );

  const valid = contactName.trim().length > 0 && title.trim().length > 0;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Log a call"
      footer={
        <Button
          className="w-full"
          disabled={!valid || create.isPending}
          onClick={() =>
            create.mutate({
              contactName: contactName.trim(),
              contactPhone: contactPhone.trim() || undefined,
              contactEmail: contactEmail.trim() || undefined,
              addressText: addressText.trim() || undefined,
              title: title.trim(),
              details: details.trim() || undefined,
              urgency,
              source: 'phone',
              ...idem(),
            })
          }
        >
          {create.isPending ? 'Saving…' : 'Save it'}
        </Button>
      }
    >
      <div className="space-y-3">
        {create.error ? <Notice tone="danger">{create.error.message}</Notice> : null}

        <Field label="Who called">
          <input
            className={field}
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Dana Whitfield"
            autoFocus
          />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Phone">
            <input
              className={field}
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              inputMode="tel"
              placeholder="614-555-0134"
            />
          </Field>
          <Field label="Email">
            <input
              className={field}
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              inputMode="email"
              placeholder="dana@example.com"
            />
          </Field>
        </div>

        <Field label="What's wrong">
          <input
            className={field}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="No hot water since this morning"
          />
        </Field>

        <Field label="How urgent">
          <select
            className={field}
            value={urgency}
            onChange={(e) => setUrgency(e.target.value as 'low' | 'normal' | 'emergency')}
          >
            <option value="low">Whenever suits</option>
            <option value="normal">Normal</option>
            <option value="emergency">Emergency</option>
          </select>
        </Field>

        <Field label="Address">
          <input
            className={field}
            value={addressText}
            onChange={(e) => setAddressText(e.target.value)}
            placeholder="482 Maple Ct, Powell"
          />
        </Field>

        <Field label="Anything else they said">
          <textarea
            className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Heard a bang, then nothing. Water heater is in the garage. Home after 3."
          />
        </Field>
      </div>
    </Sheet>
  );
}
